import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('keya_token'));
  const [loading, setLoading] = useState(true);

  const api = useCallback(() => {
    const instance = axios.create({ baseURL: API });
    if (token) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    return instance;
  }, [token]);

  useEffect(() => {
    if (token) {
      api().get('/auth/me')
        .then(res => setUser(res.data))
        .catch(() => { localStorage.removeItem('keya_token'); setToken(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, api]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('keya_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('keya_token');
    sessionStorage.removeItem('keya_skip_ack_confirm');
    sessionStorage.removeItem('keya_skip_resolve_confirm');
    setToken(null);
    setUser(null);
  };

  const hasModule = (module) => {
    const mods = user?.allowed_modules || [];
    return mods.includes(module);
  };

  // Two questions the pages keep asking. isSuper is the client's own account: the API keys, the
  // delivery settings, the user list, the reset. canUpload is that plus the Admin level, which
  // exists to run the data - every upload, and the buttons that rebuild and resend a report.
  const isSuper = user?.role === 'superadmin';
  const canUpload = isSuper || user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, api: api(), hasModule, isSuper, canUpload }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
